/**
 * VariantsTabContent — Variants tab for the UnifiedBottomPanel.
 *
 * Adaptive grid of variant previews:
 *   - landscape / square base aspect → 2 columns of cells (compact, wide).
 *   - portrait base aspect → 3 columns of cells (more cells per row offsets
 *     the height each cell takes).
 *
 * Cells render at the supplied `aspectRatio` (the grid's base aspect), with
 * newest-first ordering, version labels (v1, v2...), lit-up accent border on
 * selected, pending spinners for in-flight generations, and an empty state.
 *
 * Dynamic height: 1–2 variants = 1 row, 3–4 = 2 rows, 5+ = 2.5× capped with scroll.
 *
 * Sprint 9h: initial implementation
 * Sprint 10e: aspect-ratio thumbnails + dynamic height
 * RFC-0581 round 4: adaptive column count
 * RFC-1146: favourite/dislike marker + Like/Neutral/Dislike filter
 * Canvas UI 2.0: binary star + Variants/Archived tabs (replaces the filter row)
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../../lib/utils'
import { getThumbnailUrl, getAssetUrl } from '../../../../api/useAssetUrl'
import type { AgentColorConfig } from '../../../../constants/agentColors'
import type { HistoryEntry } from './types'
import { resolveVariantSelectionColor } from './designerUtils'
import { useNodeIdentityBorderTrace } from './useNodeIdentityBorderTrace'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { useVariantMarkers } from '../../../../hooks/useVariantMarkers'
import { VariantMarkerButton } from './VariantMarkerButton'
import { VariantsGridSkeleton } from './VariantsGridSkeleton'
import {
  VariantTabChips,
  VARIANT_TAB_ROW_HEIGHT,
} from './VariantTabChips'
import { VariantImageOverlayButtons } from './VariantImageOverlayButtons'
import {
  countVariantTabs,
  DEFAULT_VARIANT_TAB,
  findOrphanMarkerKeys,
  getVariantKey,
  isArchivedMarker,
  selectVariantsForTab,
  type VariantTab,
} from './variantKey'
import { createNadDebug } from '../../../../utils/debugLog'

const filterDebug = createNadDebug('dw:variant-marker:filter')
// Module-level dedupe so StrictMode double-mounts and unrelated re-renders do
// not flood the log with identical filter traces (mirrors
// VideoVariantsTabContent's SEEN_RENDER_KEYS).
const SEEN_FILTER_KEYS = new Set<string>()

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VariantsTabContentProps {
  /** Full history array (original order: oldest-first) */
  history: HistoryEntry[]
  /** Currently picked variant index (in original array order) */
  pickedIndex: number
  /** Selection callback (receives original array index) */
  onSelectVariant: (index: number) => void
  /** Download callback for the clicked variant path and original history index. */
  onDownloadVariant: (assetPath: string, index: number) => void | Promise<void>
  /** Number of in-flight pending generations */
  batchPending: number
  /** Cache-bust key for thumbnail URLs */
  cacheKey?: string
  /** Project ID for thumbnail URL construction */
  projectId?: string
  /** Owning node's ID — join key between the card and picker identity traces */
  nodeId: string
  /** Owning node's type, recorded in the identity-border trace */
  nodeType: string
  /**
   * Owning node's agent color identity. Required: the selected variant border
   * is derived from it so the picker always matches the node card border. A
   * required prop makes a future divergence a type error rather than a silent
   * wrong color (post 1579940090429955).
   */
  agentColor: AgentColorConfig
  /** CSS aspect-ratio for variant thumbnails (e.g., "16/9", "4/3", "1/1"). Defaults to "1/1". */
  aspectRatio?: string
  /** Max width in px for each variant thumbnail to match main image width */
  maxWidth?: number
  /**
   * Whether this tab is currently the visible one (RFC-1146).
   *
   * Drives the closed→open marker fetch.
   *
   * Defaults to true, which is correct for every current caller:
   * UnifiedBottomPanel wraps the panel in AnimatePresence and renders only the
   * active tab, so this component mounts exactly when the Variants view opens.
   * Mount/unmount already delivers the "next time the variant box is opened"
   * semantics. The prop exists for tests and for any future caller that keeps
   * the tab mounted while hidden.
   */
  isActive?: boolean
}

// ---------------------------------------------------------------------------
// Height helper
// ---------------------------------------------------------------------------

/**
 * Parse a CSS aspect-ratio string (e.g., "16/9") into a numeric value (width / height).
 * Returns 1 for invalid/missing input.
 */
function parseAspectRatio(ar: string): number {
  const parts = ar.split('/')
  const w = Number(parts[0])
  const h = Number(parts[1])
  if (!w || !h || w <= 0 || h <= 0) return 1
  return w / h
}

/**
 * Pick a column count for the variants grid based on the base aspect ratio.
 * Portrait grids get 3 columns (more cells per row offsets each cell's
 * height); landscape and square grids get 2 columns (cells already wide,
 * fewer columns reads better).
 */
function pickColumnCount(aspectRatio: string): number {
  return parseAspectRatio(aspectRatio) < 1 ? 3 : 2
}

/**
 * Compute the content height for the Variants tab based on variant count and aspect ratio.
 * Exported so consumers can pass this as `contentHeight` to UnifiedBottomPanel.
 *
 * @param variantCount - Total variants (history.length + batchPending)
 * @param aspectRatio - CSS aspect-ratio string (e.g., "16/9"). Defaults to "1/1".
 * @param nodeWidth - Node card width in px. Panel is calc(100% + 120px) wider.
 * @returns Height in pixels
 */
export function getVariantsContentHeight(
  variantCount: number,
  aspectRatio: string = '1/1',
  nodeWidth: number = 400,
): number {
  if (variantCount === 0) return 150

  const arValue = parseAspectRatio(aspectRatio) // width/height
  const columns = pickColumnCount(aspectRatio)
  // Panel width = max(nodeWidth + 120, 632); p-3 = 24px padding, gap-2 = 8px
  const panelWidth = Math.max(nodeWidth + 120, 632)
  const totalGap = (columns - 1) * 8
  const cellWidth = (panelWidth - 24 - totalGap) / columns
  const cellHeight = cellWidth / arValue
  const gap = 8
  const padding = 24

  const rows = Math.ceil(variantCount / columns)

  if (rows <= 2) {
    return Math.round(rows * cellHeight + (rows - 1) * gap + padding)
  }

  // 5+ variants: cap at 2.5 rows
  return Math.round(2.5 * cellHeight + 1.5 * gap + padding)
}

export interface VariantPanelLayout {
  /** Grid column count (9:16 portrait → 3, 16:9 landscape/square → 2). */
  columns: number
  /** Panel min width so each 1fr grid cell renders at ~nodeWidth (tiles match the preview). */
  minWidth: number
  /** Content-area height in px (no-scroll target met; grows to a bounded max then scrolls). */
  contentHeight: number
}

/**
 * Compute a self-consistent layout for the DEDICATED Variants panel
 * (post 1533007285123236). Both `minWidth` and `contentHeight` derive from the
 * SAME tile dimensions, so the panel size and the rendered grid never diverge
 * (the prior `getVariantsContentHeight` assumed a fixed 632px panel width and
 * could under/over-estimate when a custom `minWidth` was used).
 *
 * - Tiles render at the original preview width (`nodeWidth`), matching the post's
 *   "every variant the same size as the original preview".
 * - No-scroll target: 9:16 → 1 row of 3 (3 columns); 16:9 → 2 rows of 2 (2 columns).
 * - Beyond the no-scroll target the panel grows to `growMaxRows`, then the inner
 *   `overflow-y-auto` scrolls.
 *
 * Canvas UI 2.0: the Variants/Archived chip row is part of the panel, so its
 * height is reserved here whenever there is at least one tile.
 *
 * KNOWN LIMITATION — `variantCount` is the total across BOTH tabs. Tab state
 * lives inside `VariantsTabContent` (session-only `useState`), so no caller can
 * know the rendered count at layout time; every caller passes
 * `history.length + batchPending`. When the active tab holds only some of them
 * the panel therefore reserves height for more tiles than it renders and leaves
 * blank space below the grid.
 *
 * That is the SAFE direction of the divergence this function exists to prevent
 * (post 1533007285123236): over-reserving leaves a gap, under-reserving clips
 * the grid. Closing it properly means reporting the rendered count back up to
 * the parent, which couples panel height to filter state through a render
 * round-trip — deliberately deferred rather than bolted on here.
 *
 * @param aspectRatio CSS aspect-ratio string (e.g. "16/9").
 * @param nodeWidth   Node preview width in px (each tile matches this).
 * @param variantCount Pre-filter tile total (history + pending); see above.
 * @param options.headerHeight Extra reserved height (e.g. video Play-All header).
 * @param options.growMaxRows  Max rows before scrolling (default: portrait 2, landscape 3).
 * @param options.includeTabRow Reserve the Variants/Archived chip row (default true).
 */
export function computeVariantPanelLayout(
  aspectRatio: string,
  nodeWidth: number,
  variantCount: number,
  options: {
    headerHeight?: number
    growMaxRows?: number
    includeTabRow?: boolean
  } = {},
): VariantPanelLayout {
  const gap = 8 // gap-2
  const padding = 24 // p-3 → 12px × 2
  const arValue = parseAspectRatio(aspectRatio) // width / height
  const columns = pickColumnCount(aspectRatio)
  const tileW = Math.max(1, Math.round(nodeWidth))
  const tileH = Math.max(1, Math.round(tileW / arValue))
  const minWidth = columns * tileW + (columns - 1) * gap + padding
  const tabRowHeight =
    (options.includeTabRow ?? true) ? VARIANT_TAB_ROW_HEIGHT : 0
  const headerHeight = (options.headerHeight ?? 0) + tabRowHeight

  if (variantCount <= 0) {
    // Empty state renders no chip row, so it keeps the bare 150px.
    return { columns, minWidth, contentHeight: 150 }
  }

  const growMaxRows = options.growMaxRows ?? (columns === 3 ? 2 : 3)
  const rows = Math.max(1, Math.ceil(variantCount / columns))
  const visibleRows = Math.min(rows, growMaxRows)
  const contentHeight = Math.round(
    visibleRows * tileH + (visibleRows - 1) * gap + padding + headerHeight,
  )
  return { columns, minWidth, contentHeight }
}

/**
 * Bottom-panel width of the Character and Location designers: the width of
 * their Variants panel (two 400px 16:9 tiles), which they apply to every tab.
 * Image nodes (storyboard grids and panels) use it for Config/Refine so all
 * image-producing nodes open the same size panel.
 */
export const DESIGNER_PANEL_MIN_WIDTH = computeVariantPanelLayout('16/9', 400, 0).minWidth

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VariantsTabContent = memo(function VariantsTabContent({
  history,
  pickedIndex,
  onSelectVariant,
  onDownloadVariant,
  batchPending,
  cacheKey,
  projectId,
  nodeId,
  nodeType,
  agentColor,
  aspectRatio = '1/1',
  maxWidth,
  isActive = true,
}: VariantsTabContentProps) {
  const { t } = useTranslation('canvas')
  const visibleHistoryEntries = useMemo(
    () => history
      .map((entry, realIdx) => ({ entry, realIdx }))
      .filter(
        (item): item is { entry: HistoryEntry; realIdx: number } =>
          item.entry?.result != null,
      ),
    [history],
  )

  const {
    markersLive,
    hasLoaded: markersLoaded,
    setArchived,
  } = useVariantMarkers(projectId, nodeId, isActive)
  const [tab, setTab] = useState<VariantTab>(DEFAULT_VARIANT_TAB)
  const [fullscreenImagePath, setFullscreenImagePath] = useState<string | null>(
    null,
  )
  // Suspend canvas keyboard shortcuts while the fullscreen lightbox is open.
  // It portals to document.body and covers the canvas, so without this a stray
  // Delete/Backspace would delete the node underneath it (the regression class
  // canvasBlockingOverlays.coverage.test.ts locks down). The docked tab itself
  // does not block — same split as VideoVariantsTabContent.
  useBlockingOverlay(fullscreenImagePath !== null)

  const handleSelectTab = useCallback(
    (next: VariantTab) => {
      setTab(next)
      filterDebug({
        event: 'variant-marker:tab-selected',
        trace_id: `variant-marker:${nodeId}`,
        node_id: nodeId,
        tab: next,
      })
    },
    [nodeId],
  )

  // Ordering is deliberately UNCHANGED: newest-first, exactly as before.
  // Markers never reorder the grid — they only govern membership.
  const orderedEntries = useMemo(
    () => [...visibleHistoryEntries].reverse(),
    [visibleHistoryEntries],
  )

  const { visible: filteredEntries, hiddenCount } = useMemo(
    () =>
      selectVariantsForTab(
        orderedEntries,
        (item) => getVariantKey(item.entry.result),
        markersLive,
        tab,
      ),
    [orderedEntries, markersLive, tab],
  )

  // Chip counts read the LIVE map so starring a variant updates its counter
  // immediately, even though tab membership stays snapshot-based.
  const counts = useMemo(
    () =>
      countVariantTabs(
        orderedEntries,
        (item) => getVariantKey(item.entry.result),
        markersLive,
      ),
    [orderedEntries, markersLive],
  )

  // In-flight generations are new, so they are starred by definition and belong
  // to the Variants tab only. Rendering their spinners under Archived would
  // claim the user had archived something that does not exist yet.
  const pendingInTab = tab === 'variants' ? batchPending : 0
  // Post-partition count — this is what the grid actually renders, and what the
  // parent must have passed to computeVariantPanelLayout.
  const variantCount = filteredEntries.length + pendingInTab
  const totalCount = visibleHistoryEntries.length + batchPending

  useEffect(() => {
    const key = `${nodeId}|${tab}|${filteredEntries.length}|${hiddenCount}`
    if (SEEN_FILTER_KEYS.has(key)) return
    SEEN_FILTER_KEYS.add(key)
    filterDebug({
      event: 'variant-marker:tab-applied',
      node_id: nodeId,
      tab,
      rendered_count: filteredEntries.length,
      hidden_count: hiddenCount,
      variants_count: counts.variants,
      archived_count: counts.archived,
    })
  }, [nodeId, tab, filteredEntries.length, hiddenCount, counts])

  // Identity diagnostic: marker keys with no matching variant. Deliberately NOT
  // deduped by the module-level set above — a spike means identity drift and
  // must be emitted whenever it changes, not once per (node, tab) shape.
  useEffect(() => {
    const orphans = findOrphanMarkerKeys(
      orderedEntries,
      (item) => getVariantKey(item.entry.result),
      markersLive,
    )
    if (orphans.length > 0) {
      filterDebug({
        event: 'variant-marker:orphan-key',
        trace_id: `variant-marker:${nodeId}`,
        node_id: nodeId,
        orphan_count: orphans.length,
        local_paths: orphans.slice(0, 5).join(','),
      })
    }
  }, [nodeId, orderedEntries, markersLive])
  // Same rule the node card border uses when selected — see
  // resolveVariantSelectionColor / computeBorderStyle in designerUtils.
  const selectedBorderColor = resolveVariantSelectionColor(agentColor)

  // Feature trace: same tag/fields as the node card, with surface=variant-picker,
  // so `nad trace --feature node-identity-border` shows both sides per node_id.
  useNodeIdentityBorderTrace({
    nodeId,
    nodeType,
    surface: 'variant-picker',
    agentColor,
    appliedBorderColor: selectedBorderColor,
    variantIndex: pickedIndex,
  })

  // "No variants yet" is about the total across BOTH tabs: with variants
  // present but all sitting in the other tab we must still render the chip row,
  // or the user has no way back to them and the panel looks permanently broken.
  if (totalCount === 0) {
    return (
      <div
        className="h-full flex flex-col items-center justify-center text-center"
        data-testid="variants-empty-state"
      >
        <svg
          className="w-8 h-8 text-text-muted/30 mb-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <p className="text-xs font-medium text-text-muted">{t('variantsTabContent.noVariantsYet')}</p>
        <p className="text-xxs text-text-muted/60 mt-1">
          {t('variantsTabContent.generateToCreateVariants')}
        </p>
      </div>
    )
  }

  const columnCount = pickColumnCount(aspectRatio)
  return (
    <div
      className="h-full p-3 overflow-y-auto nowheel"
      data-testid="variants-tab-content"
    >
      {/* Markers not in yet: hold the chips AND the grid. Rendering either now
          would state a confident wrong answer — everything under Variants,
          "2 / 0" — and then visibly correct itself once the fetch lands. */}
      {!markersLoaded ? (
        <VariantsGridSkeleton
          tileCount={totalCount}
          columns={columnCount}
          aspectRatio={aspectRatio}
          maxWidth={maxWidth}
        />
      ) : (
      <>
      {/* Variants / Archived chips. Rendered whenever any variant exists, even
          if the active tab is empty — otherwise the user could not switch back
          to the tab that holds them. */}
      <VariantTabChips
        tab={tab}
        onSelect={handleSelectTab}
        counts={counts}
      />

      {variantCount === 0 ? (
        <div
          className="flex flex-col items-center justify-center text-center py-6"
          data-testid="variants-tab-empty-state"
        >
          <p className="text-xs font-medium text-text-muted">
            {t(`variantMarker.tabEmpty.${tab}`, { count: hiddenCount })}
          </p>
        </div>
      ) : (
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
      >
        {/* Pending spinners (newest-first → appear first). Variants tab only. */}
        {Array.from({ length: pendingInTab }).map((_, i) => (
          <div
            key={`pending-${i}`}
            className="rounded-lg bg-surface-2 flex items-center justify-center border border-border-subtle"
            style={{ aspectRatio, ...(maxWidth ? { maxWidth } : {}) }}
            data-testid="variant-pending"
          >
            <div className="w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ))}

        {/* History items — newest first, order UNCHANGED by markers. */}
        {filteredEntries.map(({ entry, realIdx }) => {
          const isSelected = realIdx === pickedIndex
          const entryResult = entry.result
          const imagePath = entryResult?.local_path
          const thumbUrl =
            imagePath && projectId
              ? getThumbnailUrl(projectId, imagePath, 200, cacheKey)
              : null
          const variantKey = getVariantKey(entryResult)
          // Star reads from the LIVE map so a click updates instantly; the tab
          // partition above reads from the snapshot so the tile does not jump.
          const archived = isArchivedMarker(
            variantKey === null ? undefined : markersLive.get(variantKey),
          )

          return (
            // A <div role="button"> rather than a <button>: the star and the
            // download/fullscreen overlay are themselves <button>s, and nesting
            // a button inside a button is invalid HTML that React warns about.
            // Mirrors VideoHistoryCell, which is already a clickable <div>.
            <div
              key={variantKey ?? `idx-${realIdx}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelectVariant(realIdx)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelectVariant(realIdx)
                }
              }}
              className={cn(
                'group relative rounded-lg overflow-hidden transition-all cursor-pointer',
                'border-2',
                isSelected
                  ? ''
                  : 'border-transparent hover:border-border'
              )}
              style={{
                aspectRatio,
                width: '100%',
                ...(maxWidth ? { maxWidth } : {}),
                ...(isSelected ? { borderColor: selectedBorderColor } : undefined),
              }}
              data-testid={`variant-item-${realIdx}`}
            >
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={`v${realIdx + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover rounded-md"
                />
              ) : (
                <div
                  className="w-full h-full bg-surface-2 flex items-center justify-center rounded-md"
                >
                  <svg
                    className="w-5 h-5 text-text-muted/30"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16"
                    />
                  </svg>
                </div>
              )}

              {/* Version label pill — TOP-LEFT, the one corner that is free on
                  every variant tile (media controls and star top-right, and on
                  video cells a full-width transport row along the bottom). Image and video grids use the same corner
                  so the version number never moves between node types. */}
              <div className="absolute top-1.5 left-1.5 text-xxs text-white bg-black/60 px-1.5 py-0.5 rounded">
                v{realIdx + 1}
              </div>

              {/* Media controls and star top-RIGHT: [expand, download, star],
                  the same slot the video grid gives the star at the end of
                  its overlay cluster. */}
              <VariantImageOverlayButtons
                imagePath={imagePath}
                onExpand={setFullscreenImagePath}
                onDownload={() => {
                  if (imagePath) return onDownloadVariant(imagePath, realIdx)
                }}
                variantIndex={realIdx}
                trailingControls={
                  <VariantMarkerButton
                    archived={archived}
                    onToggle={(nextArchived) => {
                      if (variantKey !== null) {
                        void setArchived(variantKey, nextArchived, {
                          nodeType,
                          historyIndex: realIdx,
                          variantCountAfter: counts.variants + (nextArchived ? -1 : 1),
                        })
                      }
                    }}
                    disabled={variantKey === null}
                    variantIndex={realIdx}
                  />
                }
              />
            </div>
          )
        })}
      </div>
      )}
      </>
      )}

      {/* Fullscreen image lightbox — portalled to body so it escapes the
          panel's overflow/scroll container. */}
      {fullscreenImagePath &&
        projectId &&
        createPortal(
          <div
            data-testid="variant-image-fullscreen-modal"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setFullscreenImagePath(null)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                data-testid="variant-image-fullscreen-close"
                onClick={() => setFullscreenImagePath(null)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={getAssetUrl(projectId, fullscreenImagePath) ?? undefined}
                alt=""
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
})

VariantsTabContent.displayName = 'VariantsTabContent'

export { VariantsTabContent }
